"use client"

import { useCallback, useEffect, type RefObject } from "react"

/**
 * Hook that calls a callback when a pointer-down lands outside the ref element.
 * pointerdown (not mousedown) so touch and pen dismiss overlays too.
 * @param ref - React ref object pointing to the element
 * @param callback - Function to call when an outside press is detected
 */
export const useClickOutside = <T extends HTMLElement>(ref: RefObject<T | null>, callback: () => void) => {
  const memoizedCallback = useCallback(callback, [callback])

  useEffect(() => {
    const handlePointerDownOutside = (event: PointerEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        memoizedCallback()
      }
    }

    document.addEventListener("pointerdown", handlePointerDownOutside)
    return () => {
      document.removeEventListener("pointerdown", handlePointerDownOutside)
    }
  }, [ref, memoizedCallback])
}
